import React, { useEffect, useRef, useState } from "react";
import {
  faChartLine,
  faCog,
  faHome,
  faPiggyBank,
  faWallet,
  faUsers,
  faExchangeAlt,
  faCalendarAlt,
  faBullseye,
  faList,
  faGift,
  faUser,
  faSignOutAlt,
  faMoon,
  faSun,
  faHistory,
  faTags,
  faLayerGroup,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { motion, AnimatePresence } from "framer-motion";

// Framer Motion variants for menus
const menuVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      y: { type: "spring", stiffness: 300, damping: 30 },
      scale: { type: "spring", stiffness: 300, damping: 30 },
      opacity: { duration: 0.2 },
    },
  },
  exit: { opacity: 0, y: 20, scale: 0.95, transition: { duration: 0.2 } },
};

function MenuPanel({ isOpen, onClose, children, className }) {
  // Close on click outside logic is handled by the parent or a backdrop if needed
  // For this design, we'll rely on the parent's ref logic or add a backdrop
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={menuVariants}
          className={`absolute bottom-20 left-0 right-0 mx-auto max-w-2xl z-50`}
        >
          <div
            className={`bg-white/90 dark:bg-dark-surface/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 dark:border-gray-700 overflow-hidden ${className}`}
          >
            {children}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function BottomMenuMobile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeMenu, setActiveMenu] = useState(null); // 'wallet', 'dashboard', 'settings', 'user' or null

  const navRef = useRef(null);
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [cachedImage, setCachedImage] = useState(null);
  const [totalBalance, setTotalBalance] = useState(null);
  const [totalSavingBalance, setTotalSavingBalance] = useState(null);
  const [monthlyPaidCount, setMonthlyPaidCount] = useState(null);
  const [monthlyTotalCount, setMonthlyTotalCount] = useState(null);

  // Management tabs
  const tabs = [
    { key: "accounts", label: t("accountManagement", "Accounts"), icon: faWallet },
    { key: "savings", label: t("savingAccountManagement", "Saving accounts"), icon: faPiggyBank },
    { key: "saving-goals", label: t("savingGoals", "Saving goals"), icon: faBullseye },
    { key: "exchange-rates", label: t("exchangeRates", "Exchange rates"), icon: faExchangeAlt },
    { key: "allowed-users", label: t("allowedUsers", "Користувачі"), icon: faUsers },
    { key: "mcc-codes", label: t("mccCodes", "MCC Коди"), icon: faLayerGroup },
  ];

  // Close menus on click outside
  useEffect(() => {
    function handleOutside(e) {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setActiveMenu(null);
      }
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setActiveMenu(null);
  }, [location.pathname]);

  const getActiveIndex = (pathname) => {
    if (pathname === "/" || pathname === "") return 0;
    if (pathname.startsWith("/balance") || pathname.startsWith("/savings") || pathname.startsWith("/monthly-payments"))
      return 1;
    if (pathname.startsWith("/dashboard")) return 2;
    if (pathname.startsWith("/shopping-list")) return 3;
    if (pathname.startsWith("/wish-list")) return 4;
    if (pathname.startsWith("/management")) return 5;
    return 6;
  };

  const activeIndex = getActiveIndex(location.pathname);

  const toggleMenu = (menu) => {
    setActiveMenu(activeMenu === menu ? null : menu);
  };

  const goTo = (path) => {
    setActiveMenu(null);
    navigate(path);
  };

  // Cache user image (simplified for brevity, keeping logic)
  useEffect(() => {
    if (!user?.picture) return;
    const key = `user_avatar_${user?.id || user?.email || "unknown"}`;
    const stored = localStorage.getItem(key);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.dataUrl) setCachedImage(parsed.dataUrl);
      } catch (e) {}
    } else {
      // Fetch logic omitted for brevity, assuming it works or can be restored if critical
      // Re-implementing basic fetch to ensure functionality
      fetch(user.picture)
        .then((r) => r.blob())
        .then((b) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            setCachedImage(reader.result);
            localStorage.setItem(key, JSON.stringify({ dataUrl: reader.result, ts: Date.now() }));
          };
          reader.readAsDataURL(b);
        })
        .catch(() => {});
    }
  }, [user?.picture, user?.id, user?.email]);

  // Fetch balances when wallet menu is open
  useEffect(() => {
    if (activeMenu !== "wallet") return;

    const fetchBalances = async () => {
      try {
        const [bResp, sResp, mResp] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/total-balance`),
          axios.get(`${process.env.REACT_APP_API_URL}/total-saving-balance`),
          axios.get(`${process.env.REACT_APP_API_URL}/monthly-payments`),
        ]);
        setTotalBalance(bResp?.data?.totalBalance);
        setTotalSavingBalance(sResp?.data?.totalSavingBalance);
        const payments = Array.isArray(mResp?.data) ? mResp.data : [];
        setMonthlyPaidCount(payments.filter((p) => p.paid).length);
        setMonthlyTotalCount(payments.length);
      } catch (err) {
        console.error(err);
      }
    };
    fetchBalances();
  }, [activeMenu]);

  const navItems = [
    { icon: faHome, label: "Home", action: () => goTo("/") },
    { icon: faWallet, label: "Wallet", action: () => toggleMenu("wallet"), hasMenu: true, menuId: "wallet" },
    {
      icon: faChartLine,
      label: "Dashboard",
      action: () => toggleMenu("dashboard"),
      hasMenu: true,
      menuId: "dashboard",
    },
    { icon: faList, label: "Shopping", action: () => goTo("/shopping-list") },
    { icon: faGift, label: "Wishlist", action: () => goTo("/wish-list") },
    { icon: faCog, label: "Settings", action: () => toggleMenu("settings"), hasMenu: true, menuId: "settings" },
    { icon: faUser, label: "User", action: () => toggleMenu("user"), hasMenu: true, menuId: "user" },
  ];

  return (
    <div className="fixed left-4 right-4 bottom-6 z-50 lg:hidden" ref={navRef}>
      {/* Menus */}
      <MenuPanel isOpen={activeMenu === "wallet"} className="p-2">
        <MenuItem
          icon={faWallet}
          label={t("totalBalance", "Total balance")}
          value={totalBalance != null ? `${Number(totalBalance).toFixed(0)}€` : "—"}
          isActive={location.pathname.startsWith("/balance")}
          onClick={() => goTo("/balance")}
        />
        <MenuItem
          icon={faPiggyBank}
          label={t("savingAccounts", "Saving accounts")}
          value={totalSavingBalance != null ? `${Number(totalSavingBalance).toFixed(0)}€` : "—"}
          isActive={location.pathname.startsWith("/savings")}
          onClick={() => goTo("/savings")}
        />
        <MenuItem
          icon={faCalendarAlt}
          label={t("monthlyPayments", "Monthly payments")}
          value={monthlyPaidCount != null ? `${monthlyPaidCount}/${monthlyTotalCount}` : "—"}
          isActive={location.pathname.startsWith("/monthly-payments")}
          onClick={() => goTo("/monthly-payments")}
        />
      </MenuPanel>

      <MenuPanel isOpen={activeMenu === "dashboard"} className="p-2">
        <MenuItem
          icon={faHistory}
          label={t("spendingHistory")}
          isActive={location.pathname.startsWith("/dashboard/spending-history")}
          onClick={() => goTo("/dashboard/spending-history")}
        />
        <MenuItem
          icon={faTags}
          label={t("spendingCategories")}
          isActive={location.pathname.startsWith("/dashboard/spending-categories")}
          onClick={() => goTo("/dashboard/spending-categories")}
        />
      </MenuPanel>

      <MenuPanel isOpen={activeMenu === "settings"} className="p-2 max-h-80 overflow-y-auto">
        {tabs.map((tab) => (
          <MenuItem
            key={tab.key}
            icon={tab.icon}
            label={tab.label}
            isActive={location.pathname.startsWith(`/management/${tab.key}`)}
            onClick={() => goTo(`/management/${tab.key}`)}
          />
        ))}
      </MenuPanel>

      <MenuPanel isOpen={activeMenu === "user"} className="p-4">
        <div className="flex items-center gap-3 mb-4">
          {cachedImage || user?.picture ? (
            <img
              src={cachedImage || user?.picture}
              alt="User"
              className="w-12 h-12 rounded-full object-cover border-2 border-primary-100"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center text-primary-600">
              <FontAwesomeIcon icon={faUser} size="lg" />
            </div>
          )}
          <div>
            <div className="font-bold text-gray-900 dark:text-white">{user?.name || "User"}</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">{user?.email}</div>
          </div>
        </div>

        <button
          onClick={toggleTheme}
          className="w-full py-2 px-4 mb-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center justify-center gap-2"
        >
          <FontAwesomeIcon icon={theme === 'light' ? faMoon : faSun} />
          {theme === 'light' ? t("darkMode", "Dark Mode") : t("lightMode", "Light Mode")}
        </button>

        <button
          onClick={() => {
            logout();
            navigate("/login");
          }}
          className="w-full py-2 px-4 bg-red-50 text-red-600 rounded-xl font-medium hover:bg-red-100 transition-colors flex items-center justify-center gap-2"
        >
          <FontAwesomeIcon icon={faSignOutAlt} />
          {t("logout", "Вийти")}
        </button>
      </MenuPanel>

      {/* Navigation Bar */}
      <nav className="bg-white/80 dark:bg-dark-surface/80 backdrop-blur-xl rounded-2xl shadow-glass border border-white/40 dark:border-gray-700 flex items-center justify-between px-2 h-16 relative transition-colors">
        {navItems.map((item, index) => {
          const isActive = activeIndex === index;
          return (
            <button
              key={index}
              onClick={item.action}
              className={`relative w-full h-full flex flex-col items-center justify-center gap-1 transition-colors duration-200 ${
                isActive ? "text-primary-600 dark:text-primary-400" : "text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              }`}
            >
              {isActive && (
                <motion.div
                  layoutId="navHighlight"
                  className="absolute inset-1 bg-primary-50 dark:bg-primary-900/30 rounded-xl -z-10"
                  transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                />
              )}
              <FontAwesomeIcon
                icon={item.icon}
                className={`text-lg ${isActive ? "scale-110" : "scale-100"} transition-transform`}
              />
              {/* Optional: Show label on active or just icon? For sleekness, maybe just icon or very small label. Let's keep it icon-only for now but maybe a dot for active */}
              {isActive && (
                <motion.div layoutId="activeDot" className="w-1 h-1 bg-primary-600 rounded-full absolute bottom-2" />
              )}
            </button>
          );
        })}
      </nav>
    </div>
  );
}

function MenuItem({ icon, label, value, isActive, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center justify-between p-3 rounded-xl transition-all duration-200 ${
        isActive ? "bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300" : "hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
      }`}
    >
      <div className="flex items-center gap-3">
        <div
          className={`w-8 h-8 rounded-lg flex items-center justify-center ${
            isActive ? "bg-primary-100 dark:bg-primary-900/50 text-primary-600 dark:text-primary-400" : "bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
          }`}
        >
          <FontAwesomeIcon icon={icon} />
        </div>
        <span className="font-medium">{label}</span>
      </div>
      {value && (
        <span className={`text-sm font-semibold ${isActive ? "text-primary-600 dark:text-primary-400" : "text-gray-500 dark:text-gray-400"}`}>{value}</span>
      )}
    </button>
  );
}
